import { create } from 'zustand';
import { api, type SessionPayload, type SessionUser, type SessionCompany } from '@/lib/api';

/**
 * Session state only. Company context comes from the URL (/c/:companySlug) —
 * there is no ambient tenant state and no theme toggle (light theme only).
 */
interface AppState {
  /** Set once the initial /auth/me bootstrap completes. */
  ready: boolean;

  user: SessionUser | null;
  companies: SessionCompany[];

  hydrate: (p: SessionPayload) => void;
  bootstrap: () => Promise<void>;
  signOut: () => Promise<void>;
}

export const useAppStore = create<AppState>()((set) => ({
  ready: false,
  user: null,
  companies: [],

  hydrate: (p) => set({ user: p.user, companies: p.companies, ready: true }),

  bootstrap: async () => {
    try {
      const p = await api.me();
      set({ user: p.user, companies: p.companies, ready: true });
    } catch {
      set({ user: null, companies: [], ready: true });
    }
  },

  signOut: async () => {
    try {
      await api.logout();
    } finally {
      set({ user: null, companies: [] });
    }
  },
}));

/** True when the signed-in user is WarriorLab staff. */
export const useIsStaff = () => useAppStore((s) => s.user?.kind === 'STAFF');

/** The user's role within one company: 'STAFF' | CompanyRole | null (no access). */
export const useCompanyRole = (companyId: string | undefined) =>
  useAppStore((s) =>
    s.user?.kind === 'STAFF' ? ('STAFF' as const) : (s.companies.find((c) => c.id === companyId)?.role ?? null),
  );

/** True when the user can see/decide review items anywhere (staff, or ADMIN in ≥1 company). */
export const useCanReview = () =>
  useAppStore((s) => s.user?.kind === 'STAFF' || s.companies.some((c) => c.role === 'ADMIN'));
